import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  Signal,
  signal,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { take } from 'rxjs';

import { emailValidator } from '@core/common-legacy';
import { InfoboxComponent } from '@core/components-infobox-legacy';
import { injectTranslate, TranslatePipe } from '@core/i18n-legacy';
import {
  TellentAccessService,
  TellentManageRedirectService,
  TellentMembersDataFacade,
} from '@core/tellent-logic';
import {
  BulkUpdateMembersPayload,
  InviteExistingMemberForPreonboardingForm,
  InviteMethod,
  SeatKind,
  TellentMember,
  UserInvitationFlowData,
} from '@core/tellent-types';
import { resolveMemberFullName } from '@core/tellent-util';
import {
  AvatarComponent,
  ButtonComponent,
  ButtonLoadingDirective,
  DialogBodyComponent,
  DialogComponentI,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  FormErrorDirective,
  FormFieldComponent,
  InputComponent,
  LabelComponent,
  ProtectedControlDirective,
  SpinnerComponent,
} from '@core/user-interface-legacy';
import { injectUntilDestroyed } from '@core/utils-legacy';

import { EditMembersActionsService } from '../edit-member-admin-dialog/edit-member-header/edit-members-actions.service';

@Component({
  selector: 'tlc-invite-existing-member-for-preonboarding-dialog',
  templateUrl: './invite-existing-member-for-preonboarding-dialog.component.html',
  styleUrls: ['./invite-existing-member-for-preonboarding-dialog.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DialogHeaderComponent,
    TranslatePipe,
    DialogBodyComponent,
    ButtonComponent,
    ButtonLoadingDirective,
    DialogFooterComponent,
    ReactiveFormsModule,
    AvatarComponent,
    SpinnerComponent,
    FormFieldComponent,
    InputComponent,
    LabelComponent,
    ProtectedControlDirective,
    FormErrorDirective,
    NgTemplateOutlet,
    InfoboxComponent,
  ],
  providers: [TellentManageRedirectService],
})
export class InviteExistingMemberForPreonboardingDialogComponent implements DialogComponentI<
  any,
  any
> {
  private readonly membersDataFacade = inject(TellentMembersDataFacade);
  private readonly fb = inject(FormBuilder);
  private readonly dialogRef = inject(DialogRef);
  private readonly untilDestroyed = injectUntilDestroyed();
  private readonly t = injectTranslate();
  private readonly tellentAccessService = inject(TellentAccessService);
  private readonly editMembersActionsService = inject(EditMembersActionsService);
  private readonly redirectService = inject(TellentManageRedirectService);

  protected readonly InviteMethod = InviteMethod;
  protected readonly emailFieldErrors = {
    required: this.t('tlc.common.errors.required'),
    email: this.t('tlc.common.errors.email.format'),
    duplicated: this.t('tlc.common.errors.duplicated_email'),
  };

  public formGroup: FormGroup<InviteExistingMemberForPreonboardingForm>;

  public memberData: Signal<TellentMember | null>;
  public canManageTellent: Signal<boolean> = this.tellentAccessService.canManageTellent;

  public loading = signal<boolean>(false);
  public dialogData = signal<UserInvitationFlowData | null>(null);

  protected fullName = computed<string>(() => {
    const member = this.memberData();
    return member ? resolveMemberFullName(member) : '';
  });

  public setDialogData(data: any): void {
    this.memberData = this.membersDataFacade.teamMemberById(data.memberGuid);
    this.dialogData.set(data);
  }

  public ngOnInit(): void {
    const member = this.memberData();

    this.formGroup = this.fb.group<InviteExistingMemberForPreonboardingForm>({
      personalEmail: this.fb.control(
        { value: member?.personalEmail ?? '', disabled: !!member?.personalEmail },
        [Validators.required, emailValidator],
      ),
      firstName: this.fb.control({ value: member?.firstName ?? '', disabled: true }),
      lastName: this.fb.control({ value: member?.lastName ?? '', disabled: true }),
    });
  }

  public cancel(): void {
    this.dialogRef.close();
  }

  public submit(): void {
    this.loading.set(true);

    const payload: BulkUpdateMembersPayload = this.prepareActionPayload();

    this.editMembersActionsService.inviteExistingMember(payload);

    this.handleSubmitSuccess();
    this.handleSubmitFail();
  }

  private handleSubmitSuccess(): void {
    this.membersDataFacade.inviteExistingMemberSuccess$
      .pipe(take(1), this.untilDestroyed)
      .subscribe(({ payload }) => {
        this.formGroup.markAsPristine();
        this.loading.set(false);
        const memberGuid = payload[0].guid;
        this.redirectService.sendMembersForPreonboarding([memberGuid]);
        this.dialogRef.close({ success: true, payload });
      });
  }

  private handleSubmitFail(): void {
    this.membersDataFacade.inviteExistingMemberFail$
      .pipe(take(1), this.untilDestroyed)
      .subscribe(() => {
        this.loading.set(false);
      });
  }

  private prepareActionPayload(): BulkUpdateMembersPayload {
    const { guid, seats } = this.memberData()!;
    const personalEmail = this.formGroup.getRawValue().personalEmail;
    const coreHrSeat = seats.find(seat => seat.app.kind === SeatKind.CoreHR);
    const payloadSeat = {
      appGuid: coreHrSeat?.app.guid,
      access: true,
      roleExternalId: null,
    };

    return <BulkUpdateMembersPayload>{
      members: [{ guid, personalEmail }],
      seats: coreHrSeat ? [payloadSeat] : [],
      abilities: [],
    };
  }
}
